import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatTL, formatDate, formatDateTime } from "../lib/format";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import {
  CalendarRange,
  TrendingUp,
  TrendingDown,
  Minus,
  CheckCircle2,
  ShieldAlert,
  Lightbulb,
  Wallet,
  Receipt,
  HandCoins,
  Package,
  Banknote,
} from "lucide-react";

const METRIKLER = [
  { key: "gelir", label: "Gelir", icon: Banknote, para: true },
  { key: "islem_adedi", label: "İşlem Adedi", icon: Receipt, para: false },
  { key: "tahsil_edilen", label: "Tahsil Edilen", icon: HandCoins, para: true },
  { key: "gider", label: "Faaliyet Gideri", icon: Wallet, para: true, tersRenk: true },
  { key: "maliyet", label: "Maliyet (SMM)", icon: Package, para: true, tersRenk: true },
];

/**
 * Haftalık AI Özeti — son 7 gün vs önceki 7 gün.
 * Backend GET /api/haftalik-ozet otomatik olarak AI özetini üretir (günlük cache).
 */
export const HaftalikOzet = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hata, setHata] = useState(false);

  const fetchOzet = useCallback(async () => {
    setLoading(true);
    setHata(false);
    try {
      const res = await api.get("/haftalik-ozet");
      setData(res.data);
    } catch {
      setHata(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOzet();
  }, [fetchOzet]);

  if (loading) {
    return (
      <div className="rounded-xl border bg-card p-4 space-y-3" data-testid="haftalik-ozet-loading">
        <Skeleton className="h-6 w-64" />
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (hata || !data) {
    return (
      <div className="rounded-xl border bg-card p-4 text-center text-sm text-muted-foreground" data-testid="haftalik-ozet-hata">
        Haftalık özet şu an yüklenemedi.
        <button onClick={fetchOzet} className="ml-2 font-semibold text-[hsl(var(--primary))] underline underline-offset-2" data-testid="haftalik-ozet-tekrar">
          Tekrar dene
        </button>
      </div>
    );
  }

  const { bu_hafta, onceki_hafta, degisimler, yorum } = data;
  const ic = yorum?.icerik;

  const kutular = ic
    ? [
        { baslik: "OLUMLU", icon: CheckCircle2, renk: "border-emerald-200 bg-emerald-50/60", ikonRenk: "text-emerald-600", maddeler: ic.guclu_yonler || [], testid: "haftalik-olumlu" },
        { baslik: "DİKKAT", icon: ShieldAlert, renk: "border-red-200 bg-red-50/60", ikonRenk: "text-red-600", maddeler: ic.riskler || [], testid: "haftalik-dikkat" },
        { baslik: "GELECEK HAFTA", icon: Lightbulb, renk: "border-blue-200 bg-blue-50/60", ikonRenk: "text-blue-600", maddeler: ic.oneriler || [], testid: "haftalik-oneriler" },
      ]
    : [];

  return (
    <div className="rounded-xl border bg-card overflow-hidden" data-testid="haftalik-ozet">
      {/* Başlık */}
      <div className="flex flex-wrap items-center gap-2 border-b bg-[hsl(var(--primary))] px-4 py-3">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[hsl(var(--accent))]/20">
          <CalendarRange className="h-4 w-4 text-[hsl(var(--accent))]" />
        </span>
        <h3 className="font-display text-sm font-semibold text-white">Haftalık AI Özeti</h3>
        <Badge className="bg-white/10 text-white/80 border border-white/15 hover:bg-white/10 text-[10px]" data-testid="haftalik-ozet-aralik">
          {formatDate(bu_hafta.baslangic)} – {formatDate(bu_hafta.bitis)} • önceki: {formatDate(onceki_hafta.baslangic)} – {formatDate(onceki_hafta.bitis)}
        </Badge>
        {yorum && (
          <span className="ml-auto text-[10px] text-white/60">Otomatik oluşturuldu: {formatDateTime(yorum.created_at)}</span>
        )}
      </div>

      <div className="space-y-4 p-4">
        {/* Metrik karşılaştırma kartları */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="haftalik-metrikler">
          {METRIKLER.map((m) => {
            const simdiki = bu_hafta[m.key];
            const onceki = onceki_hafta[m.key];
            const pct = degisimler[m.key];
            // Gider/maliyette artış olumsuz (kırmızı), gelirde artış olumlu (yeşil)
            const olumlu = pct == null ? null : m.tersRenk ? pct <= 0 : pct >= 0;
            return (
              <div key={m.key} className="rounded-lg border border-border/60 p-3" data-testid={`haftalik-metrik-${m.key}`}>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <m.icon className="h-3.5 w-3.5" />
                  <span className="text-[10px] font-semibold uppercase tracking-wide">{m.label}</span>
                </div>
                <p className="mt-1.5 font-display text-base font-bold tabular-nums whitespace-nowrap">
                  {m.para ? formatTL(simdiki) : simdiki.toLocaleString("tr-TR")}
                </p>
                <div className="mt-1 flex items-center gap-1.5">
                  {pct != null ? (
                    <span
                      className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${
                        olumlu ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
                      }`}
                    >
                      {pct >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      %{Math.abs(pct).toLocaleString("tr-TR", { maximumFractionDigits: 1 })}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-0.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                      <Minus className="h-3 w-3" />
                    </span>
                  )}
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                    geçen: {m.para ? formatTL(onceki) : onceki.toLocaleString("tr-TR")}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* AI özet metni */}
        {ic ? (
          <>
            <p className="text-sm leading-relaxed text-foreground/90 rounded-lg border bg-muted/30 p-3" data-testid="haftalik-ozet-metin">
              {ic.genel_degerlendirme}
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              {kutular.map((k) => (
                <div key={k.baslik} className={`rounded-xl border p-3.5 ${k.renk}`} data-testid={k.testid}>
                  <div className={`flex items-center gap-1.5 ${k.ikonRenk}`}>
                    <k.icon className="h-4 w-4" />
                    <span className="text-[11px] font-bold tracking-wide">{k.baslik}</span>
                  </div>
                  <ul className="mt-2 space-y-1.5">
                    {k.maddeler.length === 0 ? (
                      <li className="text-xs text-muted-foreground">Not bulunmuyor.</li>
                    ) : (
                      k.maddeler.map((mm, i) => (
                        <li key={i} className="text-xs leading-relaxed text-foreground/85">• {mm}</li>
                      ))
                    )}
                  </ul>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-xs text-muted-foreground" data-testid="haftalik-ozet-yok">
            Bu hafta için AI özeti henüz oluşturulamadı (yeterli veri yok veya servis geçici olarak yanıt vermedi).
          </p>
        )}
      </div>
    </div>
  );
};
